import { useRef } from 'react';

import { fg } from '@confluence/feature-gating';
import {
	CFIND_5565_CREATION_FOCUSED_NAV_XFLOW,
	CREATION_FOCUSED_NAV_SHOULD_SHOW_SPOTLIGHT,
	NAV_EXPERIMENT_ONBOARDING_KEYS,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState/onboardingState.ts';

export const d1 = 'd1';
export const loading = 'loading';
export const d0 = 'd0';
export const none = 'none';

const CREATION_FOCUSED_NAV_DELAY_MINUTES = 24 * 60; // 1440 minutes (24 hours)

type CreationFocusedNavLandResult = {
	isSpotlightVisible: boolean;
	areBannersHidden: boolean;
	navCustomization: typeof d1 | typeof loading | typeof d0 | typeof none;
	onSpotlightShown?: () => void;
};

export const useCreationFocusedNavLand = (
	updateState: boolean = false,
	skip: boolean = false,
): CreationFocusedNavLandResult => {
	const { setOnboardingState } = useSetOnboardingState();
	const delayInMinutes = CREATION_FOCUSED_NAV_DELAY_MINUTES;
	const hasUpdatedState = useRef(false);

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		fg('cfind_5565_creation_focused_nav_xflow_preload')
			? [
					...NAV_EXPERIMENT_ONBOARDING_KEYS,
					CFIND_5565_CREATION_FOCUSED_NAV_XFLOW.CHECKED_ELIGIBLE,
					CFIND_5565_CREATION_FOCUSED_NAV_XFLOW.START_AT,
				]
			: [...NAV_EXPERIMENT_ONBOARDING_KEYS],
		skip,
	);

	const spotlightShown = () => {
		setOnboardingState({
			key: CREATION_FOCUSED_NAV_SHOULD_SHOW_SPOTLIGHT,
			value: 'false',
		});
	};

	if (onboardingStateLoading) {
		// Simplify nav and hide banners while loading, to minimize flashing
		return { isSpotlightVisible: false, areBannersHidden: true, navCustomization: loading };
	}

	if (onboardingStateError) {
		// Fallback behavior if there are any errors
		return { isSpotlightVisible: false, areBannersHidden: false, navCustomization: none };
	}

	const { shouldShowSpotlight, startTime } = deserializeState(onboardingStateData);

	if (isSiteOlderThanDelay(delayInMinutes, startTime)) {
		if (shouldShowSpotlight) {
			// Spotlight ready to show --> Show spotlight, bring nav features back, but don't bring banners back
			return {
				isSpotlightVisible: true,
				areBannersHidden: true,
				navCustomization: d1,
				onSpotlightShown: spotlightShown,
			};
		} else if (shouldShowSpotlight === false) {
			// Spotlight has already shown --> Don't show spotlight, bring nav features back, but don't bring banners back
			return { isSpotlightVisible: false, areBannersHidden: true, navCustomization: d1 };
		}
		// This account was created before the feature was enabled, so we won't change anything
		return { isSpotlightVisible: false, areBannersHidden: false, navCustomization: none };
	}

	// D0 site --> set spotlight to show later, hide banners, and simplify nav
	else {
		if (shouldShowSpotlight === undefined && updateState && !hasUpdatedState.current) {
			hasUpdatedState.current = true;
			setOnboardingState({
				key: CREATION_FOCUSED_NAV_SHOULD_SHOW_SPOTLIGHT,
				value: 'true',
			});
		}
	}

	return {
		isSpotlightVisible: false,
		areBannersHidden: true,
		navCustomization: d0,
	};
};

const isSiteOlderThanDelay = (
	delayInMinutes: number,
	siteActivationDateString?: string,
): boolean => {
	if (!siteActivationDateString) {
		return true; // If site has no activation date, assume it is old
	}
	const siteActivationDateNumber = Number(siteActivationDateString);
	const siteActivationDate = new Date(siteActivationDateNumber ?? '');
	if (isNaN(siteActivationDate.getTime())) {
		return true; // If site has no activation date, assume it is old
	}
	const createdAtTimestamp = siteActivationDate.getTime();
	const millisecondsSinceCreation = Date.now() - createdAtTimestamp;
	const secondsPerMinute = 60;
	const millisecondsPerSecond = 1000;
	const millisecondsInDelayTime = delayInMinutes * secondsPerMinute * millisecondsPerSecond;
	return millisecondsSinceCreation > millisecondsInDelayTime;
};
